/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import type { ShopperBasketsV2 } from '@/scapi';
import { getCardTypeDisplay, getLastFourDigits } from '@/lib/payment/payment-utils';
import { getCardIcon } from '@/lib/payment/card-icon-utils';

interface OrderReviewPaymentMethodProps {
    className?: string;
    instrument?: ShopperBasketsV2.schemas['OrderPaymentInstrument'] | null;
}

/**
 * Renders the payment method summary block in the order review header row.
 * For credit cards: shows card brand icon + masked number (---- XXXX).
 * For PayPal / financing: shows the payment method name as text.
 * Returns null when no payment instrument is present.
 */
export default function OrderReviewPaymentMethod({ className, instrument }: OrderReviewPaymentMethodProps) {
    const { t } = useTranslation('checkout');

    if (!instrument) {
        return null;
    }

    const methodId = instrument.paymentMethodId ?? '';
    const isCard = methodId.startsWith('CREDIT_CARD') || methodId === 'CREDIT_CARD';
    const isPayPal = methodId === 'PayPal';
    const isFinance = methodId === 'ASHLEY_FINANCE' || methodId === 'ACIMA_FINANCE';

    const cardType = getCardTypeDisplay(instrument);
    const pi = instrument as {
        maskedNumber?: string;
        numberLastDigits?: string;
        c_lightBoxMaskedNumber?: string;
        c_financeCardType?: string;
        c_financeCardNumber?: string;
        paymentCard?: { maskedNumber?: string; numberLastDigits?: string };
    };
    // Converge LightBox instruments carry the masked PAN in c_lightBoxMaskedNumber (SCAPI does not
    // persist paymentCard.maskedNumber for tokenized cards); fall back to the standard fields.
    const maskedNumber = pi.c_lightBoxMaskedNumber || pi.maskedNumber || pi.paymentCard?.maskedNumber;
    const lastFour = getLastFourDigits(maskedNumber, pi.paymentCard?.numberLastDigits || pi.numberLastDigits);

    // Financing tenders (Ashley Advantage / Acima) have no card brand. Show the shopper-facing
    // brand label (the stored c_financeCardType is the issuer, e.g. "Synchrony", not the brand)
    // plus the masked account's last 4 when available.
    const mappedFinanceLabel = t(`reviewOrder.paymentMethod.methodLabels.${methodId}`, { defaultValue: '' });
    const financeLabel = mappedFinanceLabel || pi.c_financeCardType || methodId;
    const financeLastFour = getLastFourDigits(pi.c_financeCardNumber);
    const hasFinanceLastFour = /^\d{4}$/.test(financeLastFour);

    const CardIcon = getCardIcon(cardType);

    return (
        <div className={className} data-testid="order-review-paymentmethod">
            <p className="mb-4 text-xs uppercase tracking-wide text-muted-foreground">
                {t('reviewOrder.paymentMethod.heading')}
            </p>

            {isCard ? (
                <p className="flex items-center gap-2 text-sm font-bold text-foreground mb-3 leading-5">
                    <CardIcon className="h-4 w-6 shrink-0" aria-hidden />
                    <span>{t('reviewOrder.paymentMethod.maskedCard', { lastFour })}</span>
                </p>
            ) : isPayPal ? (
                <p className="text-sm font-bold text-foreground mb-3 leading-5">PayPal</p>
            ) : isFinance ? (
                <p className="text-sm font-bold text-foreground mb-3 leading-5">
                    {hasFinanceLastFour
                        ? t('reviewOrder.paymentMethod.financeCard', {
                              label: financeLabel,
                              lastFour: financeLastFour,
                          })
                        : financeLabel}
                </p>
            ) : (
                <p className="text-sm font-bold text-foreground mb-3 leading-5">{methodId}</p>
            )}

            <p className="mt-1 text-xs text-muted-foreground">{t('reviewOrder.paymentMethod.disclaimer')}</p>
        </div>
    );
}
